/**
 * Widget System Types
 *
 * A Window is a named dashboard containing Widgets.
 * A Widget is a configurable UI component with typed configuration.
 */

import type { TaskStatus } from './index';
// Note: 'projects' widget type removed — old user configs may still reference it
import type { TaskQuery } from './query';

// ============================================================================
// Widget Types - Each type has its own config shape
// ============================================================================

export type WidgetType =
  | 'task-list'
  | 'calendar-bar'
  | 'calendar-full'
  | 'day-schedule'
  | 'timeline'
  | 'weekly-review'
  | 'monthly-review'
  | 'yearly-review'
  | 'ai-chat'
  | 'habit-tracker'
  | 'goals'
  | 'notes'
  | 'daily-journal'
  | 'pomodoro'
  | 'activity-feed';

// Display fields that can be shown in task preview
export type TaskDisplayField =
  | 'status'
  | 'horizon'
  | 'date'
  | 'deadline'
  | 'tags'
  | 'description'
  | 'assignee'
  | 'progress';

// Task list widget - shows filtered todos
export interface TaskListWidgetConfig {
  title?: string;

  // Query mode: 'simple' uses UI options, 'advanced' uses raw query
  queryMode?: 'simple' | 'advanced';

  // Advanced mode: direct query (takes precedence if queryMode is 'advanced')
  query?: TaskQuery | null;
  queryText?: string; // Human-readable query text (synced with query)

  // Simple mode: UI-based filtering (used when queryMode is 'simple' or undefined)
  statusFilter?: TaskStatus[];
  horizonFilter?: string[];
  tagsFilter?: string[];
  dateFilter?: 'today' | 'this-week' | 'this-month' | 'overdue' | 'all' | 'no-date';
  dateFilters?: ('today' | 'this-week' | 'this-month' | 'overdue' | 'no-date' | 'has-date')[];
  showCompleted?: boolean;
  onlyWithDeadline?: boolean;
  onlyOverdue?: boolean;

  // Display options (apply to both modes)
  displayFields?: TaskDisplayField[];
  showTaskCount?: boolean;
  compactMode?: boolean;

  // Sorting and grouping (apply to both modes)
  groupBy?: 'none' | 'status' | 'date' | 'horizon';
  sortBy?: 'date' | 'created' | 'deadline' | 'title' | 'horizon';
  sortDirection?: 'asc' | 'desc';
  maxItems?: number;
}

// Calendar bar - compact date display
export interface CalendarBarWidgetConfig {
  showWeekNumbers?: boolean;
  highlightToday?: boolean;
}

// Full calendar - month/week calendar view
export interface CalendarFullWidgetConfig {
  defaultView?: 'month' | 'week';
  showTaskCounts?: boolean;
}

// Day schedule widget - time-based schedule view
export interface DayScheduleWidgetConfig {
  showEvents?: boolean;
  showTasks?: boolean;
  startHour?: number;
  endHour?: number;
}

// Timeline widget - chronological task view
export interface TimelineWidgetConfig {
  daysToShow?: number;
  showCompleted?: boolean;
}

// Review widgets - weekly/monthly/yearly reviews
export interface WeeklyReviewWidgetConfig {
  showGoals?: boolean;
  showReflections?: boolean;
}

export interface MonthlyReviewWidgetConfig {
  showGoals?: boolean;
  showReflections?: boolean;
}

export interface YearlyReviewWidgetConfig {
  showGoals?: boolean;
  showVision?: boolean;
}

// AI Chat widget
export interface AIChatWidgetConfig {
  model?: string;
  conversationId?: string;
}

// Habit tracker widget - daily check-off with streaks
export interface HabitTrackerWidgetConfig {
  title?: string;
  showInactive?: boolean;
  showStreaks?: boolean;
  showWeekGrid?: boolean;
}

// Goals widget - goal tracking with linked items
export interface GoalsWidgetConfig {
  title?: string;
  showAchieved?: boolean;
  showLinkedItems?: boolean;
}

// Daily journal widget - daily overview with journaling
export interface DailyJournalWidgetConfig {
  showTasks?: boolean;
  showMood?: boolean;
  showSchedule?: boolean;
}

// Pomodoro timer widget
export interface PomodoroWidgetConfig {
  title?: string;
}

// Activity feed widget - chronological day log
export interface ActivityFeedWidgetConfig {
  title?: string;
  showTaskCreations?: boolean;
  showTaskCompletions?: boolean;
  showMoodLogs?: boolean;
  showJournalEntries?: boolean;
  showAgentTaskActions?: boolean;
  showAgentEventActions?: boolean;
  showAgentHabitActions?: boolean;
  showAgentComments?: boolean;
  maxItems?: number;
}

// Notes widget - pin a journal prompt from a review period
export interface NotesWidgetConfig {
  pinnedPrompt: string;
  sourceType: 'weekly' | 'monthly' | 'daily_start' | 'daily_end' | 'daily_general' | 'yearly';
}

// Map widget types to their config
export interface WidgetConfigMap {
  'task-list': TaskListWidgetConfig;
  'calendar-bar': CalendarBarWidgetConfig;
  'calendar-full': CalendarFullWidgetConfig;
  'day-schedule': DayScheduleWidgetConfig;
  'timeline': TimelineWidgetConfig;
  'weekly-review': WeeklyReviewWidgetConfig;
  'monthly-review': MonthlyReviewWidgetConfig;
  'yearly-review': YearlyReviewWidgetConfig;
  'ai-chat': AIChatWidgetConfig;
  'habit-tracker': HabitTrackerWidgetConfig;
  'goals': GoalsWidgetConfig;
  'notes': NotesWidgetConfig;
  'daily-journal': DailyJournalWidgetConfig;
  'pomodoro': PomodoroWidgetConfig;
  'activity-feed': ActivityFeedWidgetConfig;
}

// ============================================================================
// Widget Instance - A configured widget with layout
// ============================================================================

export interface Widget<T extends WidgetType = WidgetType> {
  id: string;
  type: T;
  config: WidgetConfigMap[T];
}

// ============================================================================
// Layout - react-grid-layout format
// ============================================================================

export interface WidgetLayout {
  i: string;  // Widget id
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
  maxW?: number;
  maxH?: number;
}

// ============================================================================
// Window - A named dashboard of widgets
// ============================================================================

export interface Window {
  id: string;
  name: string;
  icon?: string;
  widgets: Widget[];
  layouts: WidgetLayout[];
  responsiveLayouts?: Record<string, WidgetLayout[]>;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// User's complete windows configuration
// ============================================================================

export interface WindowsConfig {
  windows: Window[];
  activeWindowId: string | null;
  defaultWindowId: string | null;
}

// ============================================================================
// Helpers
// ============================================================================

// Default sizes - h defaults to minH for compact initial size
export const DEFAULT_WIDGET_SIZES: Record<WidgetType, { w: number; h: number; minW: number; minH: number }> = {
  'task-list': { w: 4, h: 4, minW: 1, minH: 2 },
  'calendar-bar': { w: 12, h: 2, minW: 4, minH: 2 },
  'calendar-full': { w: 4, h: 4, minW: 3, minH: 3 },
  'day-schedule': { w: 4, h: 6, minW: 2, minH: 2 },
  'timeline': { w: 6, h: 5, minW: 4, minH: 5 },
  'weekly-review': { w: 4, h: 4, minW: 3, minH: 3 },
  'monthly-review': { w: 6, h: 6, minW: 4, minH: 6 },
  'yearly-review': { w: 6, h: 6, minW: 4, minH: 6 },
  'ai-chat': { w: 4, h: 5, minW: 2, minH: 3 },
  'habit-tracker': { w: 6, h: 6, minW: 3, minH: 4 },
  'goals': { w: 6, h: 6, minW: 3, minH: 4 },
  'notes': { w: 4, h: 3, minW: 2, minH: 2 },
  'daily-journal': { w: 4, h: 6, minW: 3, minH: 4 },
  'pomodoro': { w: 3, h: 4, minW: 2, minH: 3 },
  'activity-feed': { w: 4, h: 6, minW: 2, minH: 3 },
};

export function createWidget<T extends WidgetType>(
  type: T,
  config: Partial<WidgetConfigMap[T]> = {}
): Widget<T> {
  return {
    id: crypto.randomUUID(),
    type,
    config: config as WidgetConfigMap[T],
  };
}

export function createWindow(name: string, widgets: Widget[] = []): Window {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    name,
    widgets,
    layouts: widgets.map((widget, index) => {
      const defaults = DEFAULT_WIDGET_SIZES[widget.type];
      return {
        i: widget.id,
        x: (index * 4) % 12,
        y: Math.floor(index / 3) * 4,
        ...defaults,
      };
    }),
    createdAt: now,
    updatedAt: now,
  };
}
